import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { NoteField } from './NoteField';

/** A row's «Rifiuta» or «Rendi rimborsabile…»: asks for the mandatory note, then decides. */
export function DecisionSheet({
  title,
  help,
  confirmLabel,
  destructive,
  busy,
  noteLabel,
  noteMissing,
  maxLength,
  notice,
  onConfirm,
  onClose,
}: {
  readonly title: string;
  readonly help: string;
  readonly confirmLabel: string;
  readonly destructive: boolean;
  readonly busy: boolean;
  /** The field's label and its «missing» message; an approver's «Nota» by default. */
  readonly noteLabel?: string;
  readonly noteMissing?: string;
  readonly maxLength?: number;
  /** A refusal of the last attempt, shown in the sheet so the note typed is kept and retried. */
  readonly notice?: ReactNode;
  readonly onConfirm: (note: string) => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('expenses');
  const [note, setNote] = useState('');
  const [missing, setMissing] = useState(false);

  const confirm = () => {
    if (note.trim() === '') {
      setMissing(true);
      return;
    }
    onConfirm(note);
  };

  return (
    <Sheet open onOpenChange={(next) => (next ? undefined : onClose())}>
      <SheetContent className="flex flex-col gap-4 p-4">
        <SheetTitle>{title}</SheetTitle>
        <SheetDescription>{help}</SheetDescription>
        <NoteField
          label={noteLabel ?? t('reports.page.decision.note')}
          value={note}
          {...(maxLength === undefined ? {} : { maxLength })}
          invalidMessage={
            missing ? (noteMissing ?? t('reports.page.decision.noteMissing')) : undefined
          }
          onChange={(value) => {
            setNote(value);
            if (value.trim() !== '') setMissing(false);
          }}
        />
        {notice}
        <div className="flex gap-2">
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            disabled={busy}
            onClick={confirm}
          >
            {confirmLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('reports.page.decision.cancel')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
